/**
 * The page's one source of truth: the state, the activity feed, and who to tell when they change.
 * Mock, live and replay all feed it events; replay can also reset it to seek backwards.
 */
import type { PlatformEvent } from "@botallion/contracts";
import { describeEvent, type FeedLine } from "./describe.ts";
import { applyEvent, emptyState, type DashboardState } from "./state.ts";

export class Store {
  state: DashboardState = emptyState();
  /** Every feed line, oldest first. */
  feed: FeedLine[] = [];
  private readonly listeners = new Set<(applied: readonly PlatformEvent[]) => void>();

  /** Apply events in order and notify once. Returns the events that were new. */
  apply(events: readonly PlatformEvent[]): PlatformEvent[] {
    const fresh = this.applyQuietly(events);
    if (fresh.length) this.notify(fresh);
    return fresh;
  }

  /**
   * Replace the state and feed (a replay checkpoint), apply `then` on top, and notify once. Seeking anywhere in a
   * replay comes through here.
   */
  load(state: DashboardState, feed: FeedLine[], then: readonly PlatformEvent[] = []): void {
    this.state = state;
    this.feed = feed;
    this.notify(this.applyQuietly(then));
  }

  private applyQuietly(events: readonly PlatformEvent[]): PlatformEvent[] {
    const fresh: PlatformEvent[] = [];
    for (const e of events) {
      if (e.seq <= this.state.lastSeq) continue;
      applyEvent(this.state, e);
      fresh.push(e);
      // Described against the state just after the event, so names resolve as they did at the time.
      const line = describeEvent(this.state, e);
      if (line) this.feed.push(line);
    }
    return fresh;
  }

  /** Start again from nothing (switching mode, or seeking backwards in a replay). */
  reset(): void {
    this.state = emptyState();
    this.feed = [];
    this.notify([]);
  }

  /** The newest feed lines first. */
  latestFeed(limit: number): FeedLine[] {
    return this.feed.slice(-limit).reverse();
  }

  onChange(listener: (applied: readonly PlatformEvent[]) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(applied: readonly PlatformEvent[]): void {
    for (const l of this.listeners) l(applied);
  }
}
